/** Typed fetch client for /api. Every POST carries a fresh requestId unless the caller supplies one. */
import type { ZodType } from "zod";
import { reportApiFailure } from "./telemetry";
import { noteTones } from "./tones";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** The server's reference for this request (D-077), shown on server errors so a player can quote it. */
  readonly ref: string | undefined;
  constructor(message: string, status: number, code?: string, ref?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.ref = ref;
  }
  /** True when the request may have reached the server but we never saw the answer. */
  get uncertain(): boolean { return this.status === 0 || this.status === 504 || this.status === 502; }
}

export const NETWORK_SENTENCE = "The connection dropped. Nothing is lost. Check your signal and try again.";
export const SERVER_SENTENCE = "Something went wrong on our side. Nothing is lost. Try again in a moment.";

export const newRequestId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const UNAUTHORIZED_EVENT = "digu:unauthorized";
export const isMock = (): boolean => import.meta.env.VITE_MOCK === "1";

type MockHandler = (method: string, path: string, body: unknown) => Promise<unknown>;
let mockHandler: MockHandler | null = null;
async function loadMock(): Promise<MockHandler> {
  if (!mockHandler) { const m = await import("./mock"); mockHandler = m.handle; }
  return mockHandler;
}

export interface RequestOptions<T> { schema?: ZodType<T>; signal?: AbortSignal; silent401?: boolean }

async function request<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, body: unknown, opts: RequestOptions<T> = {}): Promise<T> {
  const url = path.startsWith("/api") ? path : `/api${path}`;
  let data: unknown;
  if (isMock()) {
    const handle = await loadMock();
    try { data = await handle(method, url.slice(4), body); }
    catch (e) { if (e instanceof ApiError) { if (e.status === 401 && !opts.silent401) notifyUnauthorized(); throw e; } throw e; }
  } else {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers: body !== undefined ? { "content-type": "application/json" } : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: "same-origin",
        signal: opts.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      reportApiFailure(url, 0, "network", undefined);
      throw new ApiError(NETWORK_SENTENCE, 0, "network");
    }
    const text = await res.text();
    let json: unknown = null;
    if (text) { try { json = JSON.parse(text); } catch { json = null; } }
    if (!res.ok) {
      // D-219: DIGU's API always answers errors as JSON. A 404 or 502–504 without that body comes from the proxy while a
      // deploy swaps the container (no route for a few seconds): treat it as a dropped connection, so a table shows
      // Reconnecting and retries instead of reading "this table is gone".
      if ((res.status === 404 || (res.status >= 502 && res.status <= 504)) && !(json && typeof json === "object" && "error" in (json as object))) {
        reportApiFailure(url, 0, "network", undefined);
        throw new ApiError(NETWORK_SENTENCE, 0, "network");
      }
      const err = (json && typeof json === "object" ? (json as { error?: string; code?: string; ref?: string }) : {}) ?? {};
      if (res.status === 401 && !opts.silent401) notifyUnauthorized();
      const ref = err.ref ?? res.headers?.get?.("x-digu-ref") ?? undefined;
      reportApiFailure(url, res.status, err.code, ref);
      const sentence = err.error || (res.status >= 500 ? SERVER_SENTENCE : NETWORK_SENTENCE);
      // Our fault: give the player the reference to quote if they get in touch.
      throw new ApiError(res.status >= 500 && ref ? `${sentence} Reference ${ref}.` : sentence, res.status, err.code, ref);
    }
    data = json;
  }
  noteTones(data);
  if (opts.schema) {
    const parsed = opts.schema.safeParse(data);
    if (!parsed.success) reportApiFailure(url, 500, "shape", undefined);
    if (!parsed.success) throw new ApiError("The game sent something this app didn't expect. Nothing is lost. Reload to try again.", 500, "shape");
    return parsed.data;
  }
  return data as T;
}

function notifyUnauthorized(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

const withRequestId = (body: unknown): unknown => {
  if (body && typeof body === "object" && !Array.isArray(body) && !("requestId" in body)) return { requestId: newRequestId(), ...body };
  return body;
};

export const api = {
  get: <T>(path: string, opts?: RequestOptions<T>) => request<T>("GET", path, undefined, opts),
  /** POST with an automatic requestId (pass your own to retry the same request). */
  post: <T>(path: string, body?: unknown, opts?: RequestOptions<T>) => request<T>("POST", path, withRequestId(body ?? {}), opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions<T>) => request<T>("PUT", path, body ?? {}, opts),
  del: <T>(path: string, opts?: RequestOptions<T>) => request<T>("DELETE", path, undefined, opts),
};

/** Turn any thrown value into a player-facing sentence. */
export const errorSentence = (e: unknown): string => (e instanceof ApiError ? e.message : e instanceof Error && e.message ? e.message : SERVER_SENTENCE);
